import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Banner, view} from '@sail/ui';
import {call, chat, email} from '@sail/icons/react/Icon';
import type {IconAsset} from '@sail/icons/types';
import {defineMessages, useIntl, type IntlShape} from 'react-intl';
import {ChatButtonOptions} from '../../Chat/Row/ChatButtonOptions';
import type {Option} from '../../Chat/Row/ChatButtonOption';
import {useAnalytics} from '../../AnalyticsProvider';
import {Form as RequestACallForm} from '../../RequestACall/Form';
import {
  SendUsAnEmailForm,
  type SendUsAnEmailFormValues,
} from '../../SendUsAnEmail/SendUsAnEmailForm';
import type {
  ChannelMenuSubmissionContent,
  MessagePartComponentProps,
} from '../MessagePartComponentContext';
import {useMessagePartHandlers} from '../MessagePartComponentProvider';
import {buildUHConversationIssueContext} from './ChannelMenuTranscriptHelpers';

type ChannelOption = {
  id: string;
  message: string;
  disabled: boolean;
  icon?: string;
};

type ChannelMenuContent = {
  channel_options: ChannelOption[];
  notice?: string;
  notice_type?: string;
  selected_option_id?: string;
  hide_restart_chat_option: boolean;
  show_subject_field: boolean;
  show_unauthenticated_email_field: boolean;
  show_cc_emails_field: boolean;
  initial_name?: string;
  initial_phone_number?: string;
  initial_unauthenticated_email?: string;
  is_rac_form_submitted?: boolean;
  is_email_form_submitted?: boolean;
  error_code?: string;
};

// Single typed seam for the framework's untyped `part.content` / action data.
function asChannelMenuContent(value: unknown): ChannelMenuContent {
  return value as ChannelMenuContent;
}

const messages = defineMessages({
  racInvalidForm: {
    id: 'conversations_ui.channel_menu.rac_invalid_form',
    description: 'Error shown when RAC form submission has invalid data',
    defaultMessage: 'Please check your information and try again.',
  },
  racRateLimitExceeded: {
    id: 'conversations_ui.channel_menu.rac_rate_limit_exceeded',
    description: 'Error shown when RAC form hits rate limit',
    defaultMessage:
      'You have submitted too many requests. Please try again later.',
  },
  racIneligible: {
    id: 'conversations_ui.channel_menu.rac_ineligible',
    description: 'Error shown when user is ineligible for RAC',
    defaultMessage:
      'Request a call is not available at this time. Please try another option.',
  },
  racGenericError: {
    id: 'conversations_ui.channel_menu.rac_generic_error',
    description: 'Generic error shown for RAC failures',
    defaultMessage: 'Something went wrong. Please try again.',
  },
  racActiveLiveSupport: {
    id: 'conversations_ui.channel_menu.rac_active_live_support',
    description:
      'Error shown when requesting a call while another conversation already has an active live chat or queued callback',
    defaultMessage:
      'You already have an ongoing request in another conversation. Please continue there.',
  },
  chatActiveLiveSupport: {
    id: 'conversations_ui.channel_menu.chat_active_live_support',
    description:
      'Error shown when starting a chat while another conversation already has an active live chat or queued callback',
    defaultMessage:
      'You already have an ongoing request in another conversation. Please continue there.',
  },
  selectionError: {
    id: 'conversations_ui.channel_menu.selection_error',
    description: 'Error shown when selecting a channel option fails',
    defaultMessage:
      'There was an issue selecting your option. Please check your internet connection and try again.',
  },
  restartTheChat: {
    id: 'conversations_ui.channel_menu.restart_the_chat',
    description: 'Button used to restart the stripe support chat.',
    defaultMessage: 'Ask about something else',
  },
});

type ChannelMenuServerError =
  | {kind: 'none'}
  | {kind: 'rac'; message: string}
  | {
      kind: 'email';
      reason:
        | 'rate_limited'
        | 'invalid_email'
        | 'invalid_cc_emails'
        | 'submission';
    };

function getIconAsset(icon: string | undefined): IconAsset | undefined {
  switch (icon) {
    case 'chat':
      return chat;
    case 'call':
      return call;
    case 'email':
      return email;
    default:
      return undefined;
  }
}

function mapNoticeType(noticeType: string | undefined): 'caution' | 'default' {
  return noticeType === 'warning' ? 'caution' : 'default';
}

function serverStringIdFromMetadata(
  data: Record<string, unknown> | undefined,
  key: string,
  fallback: string,
): string {
  const value = data?.[key];
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function serverNumberIdFromMetadata(
  data: Record<string, unknown> | undefined,
  key: string,
  fallback: number,
): number {
  const value = data?.[key];
  return typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : fallback;
}

function isSubmittedContent(content: ChannelMenuContent): boolean {
  return (
    content.is_rac_form_submitted === true ||
    content.is_email_form_submitted === true
  );
}

function mergePollUpdate(
  current: ChannelMenuContent,
  updated: ChannelMenuContent,
): ChannelMenuContent {
  return {
    ...current,
    ...updated,
    selected_option_id: current.selected_option_id,
    is_rac_form_submitted: current.is_rac_form_submitted,
    is_email_form_submitted: current.is_email_form_submitted,
  };
}

function withRestartChannelOption(
  content: ChannelMenuContent,
  intl: IntlShape,
): ChannelMenuContent {
  if (content.hide_restart_chat_option) {
    return content;
  }
  if (
    content.channel_options.some((option) => option.id === 'channel_restart')
  ) {
    return content;
  }

  return {
    ...content,
    channel_options: [
      ...content.channel_options,
      {
        id: 'channel_restart',
        message: intl.formatMessage(messages.restartTheChat),
        disabled: false,
      },
    ],
  };
}

function submissionSucceeded(response: unknown): boolean {
  if (!response || typeof response !== 'object') {
    return false;
  }

  const {code} = response as {code?: unknown};
  return typeof code === 'number' && code < 400;
}

function errorCodeFromResponse(response: unknown): string | undefined {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  const {error} = response as {error?: unknown};
  return typeof error === 'string' ? error : undefined;
}

function buildEmailSubmissionText(
  values: Pick<
    SendUsAnEmailFormValues,
    'unauthenticatedEmail' | 'question' | 'subject' | 'ccEmails'
  >,
): string {
  const lines: string[] = [];
  const emailAddress = values.unauthenticatedEmail ?? '';
  if (emailAddress.length > 0) {
    lines.push(emailAddress);
  }
  if (values.subject) {
    lines.push(values.subject);
  }
  if (values.ccEmails && values.ccEmails.length > 0) {
    lines.push(`CC: ${values.ccEmails.join(', ')}`);
  }
  lines.push(values.question ?? '');
  return lines.join('\n');
}

function parseChannelMenuServerError(
  errorCode: string | undefined,
  intl: IntlShape,
): ChannelMenuServerError {
  switch (errorCode) {
    case undefined:
      return {kind: 'none'};
    case 'rac-invalid-form':
      return {
        kind: 'rac',
        message: intl.formatMessage(messages.racInvalidForm),
      };
    case 'rac-rate-limit-exceeded':
      return {
        kind: 'rac',
        message: intl.formatMessage(messages.racRateLimitExceeded),
      };
    case 'rac-ineligible':
      return {
        kind: 'rac',
        message: intl.formatMessage(messages.racIneligible),
      };
    case 'rac-active-live-support':
      return {
        kind: 'rac',
        message: intl.formatMessage(messages.racActiveLiveSupport),
      };
    case 'email-rate-limited':
      return {kind: 'email', reason: 'rate_limited'};
    case 'email-invalid-email':
      return {kind: 'email', reason: 'invalid_email'};
    case 'email-invalid-cc-emails':
      return {kind: 'email', reason: 'invalid_cc_emails'};
    default:
      if (errorCode.startsWith('rac-')) {
        return {
          kind: 'rac',
          message: intl.formatMessage(messages.racGenericError),
        };
      }
      if (errorCode.startsWith('email-')) {
        return {kind: 'email', reason: 'submission'};
      }
      return {kind: 'none'};
  }
}

function channelSelectionProperties(conversationId: string, channelId: string) {
  return {
    conversationId,
    channelId,
  };
}

export function ChannelMenuPartComponent({
  conversation,
  message,
  part,
  animated,
  disableInteraction,
}: MessagePartComponentProps) {
  const {onSubmit} = useMessagePartHandlers();
  const intl = useIntl();
  const analytics = useAnalytics();
  const initialContent = asChannelMenuContent(part.content);
  const [content, setContent] = useState<ChannelMenuContent>(initialContent);
  const [selectedChannel, setSelectedChannel] = useState<string | undefined>(
    initialContent.selected_option_id,
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(
    isSubmittedContent(initialContent),
  );
  const [selectionError, setSelectionError] = useState<string | undefined>();
  const [clientFormError, setClientFormError] =
    useState<ChannelMenuServerError>({kind: 'none'});
  const sourceMessageId = serverStringIdFromMetadata(
    message.data,
    'sourceMessageId',
    message.id,
  );
  const sourcePartId = serverNumberIdFromMetadata(
    message.data,
    'sourcePartId',
    part.id,
  );
  const sourceCreatorPartId = `${sourceMessageId}.${sourcePartId}`;

  const hasTrackedView = useRef(false);
  const partContentRef = useRef(part.content);
  const isFormOpenRef = useRef(false);
  useEffect(() => {
    if (hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    analytics.viewed('universal_help_channel_menu_viewed', {
      conversationId: conversation.id,
    });
  }, [analytics, conversation.id]);

  useEffect(() => {
    if (partContentRef.current !== part.content) {
      partContentRef.current = part.content;
      const updated = asChannelMenuContent(part.content);
      const updatedSubmitted = isSubmittedContent(updated);
      setContent((current) =>
        isFormOpenRef.current && !updatedSubmitted
          ? mergePollUpdate(current, updated)
          : updated,
      );
      if (
        updated.selected_option_id !== undefined &&
        (!isFormOpenRef.current || updatedSubmitted)
      ) {
        setSelectedChannel(updated.selected_option_id);
      }
      if (updatedSubmitted) {
        setSubmitted(true);
      }
    }
  }, [part.content]);

  // `if_latest` means the menu is only live while its message is the most
  // recent one in the conversation. Once the conversation advances past it the
  // menu should collapse (`isDone` -> `return null`), so gate it on being the
  // latest message. `active` remains unconditionally live.
  const isLatestMessage = conversation.mostRecentMessage()?.id === message.id;
  const isLive =
    part.dynamic === 'active' ||
    (part.dynamic === 'if_latest' && isLatestMessage);
  const submittedByContent = isSubmittedContent(content);
  const isDone =
    !isLive || !!disableInteraction || submitted || submittedByContent;
  const isFormOpen =
    !submitted &&
    !submittedByContent &&
    (selectedChannel === 'channel_rac' || selectedChannel === 'channel_email');
  isFormOpenRef.current = isFormOpen;

  const displayContent = useMemo(
    () => withRestartChannelOption(content, intl),
    [content, intl],
  );

  const submitChannelMenuOutcome = useCallback(
    async (submissionContent: ChannelMenuSubmissionContent) => {
      const submissionPart = {
        type: 'channel_menu_submission' as const,
        content: submissionContent,
      };
      if (onSubmit) {
        return onSubmit({
          conversationId: conversation.id,
          submission: {
            id: `${sourceCreatorPartId}.submission`,
            creatorPartId: sourceCreatorPartId,
            parts: [{id: 1, ...submissionPart}],
          },
        });
      }

      return conversation.sendMessage(
        conversation.buildTextMessage(submissionContent.text, {
          creator_part_id: sourceCreatorPartId,
          message_part_submission: submissionPart,
        }),
      );
    },
    [conversation, onSubmit, sourceCreatorPartId],
  );

  const showSelectionFailure = useCallback(
    (errorCode: string | undefined) => {
      setSelectedChannel(undefined);
      setSelectionError(
        errorCode === 'chat-active-live-support'
          ? intl.formatMessage(messages.chatActiveLiveSupport)
          : intl.formatMessage(messages.selectionError),
      );
    },
    [intl],
  );

  const handleSelectOption = useCallback(
    async (optionId: string) => {
      if (isDone || submitting) return;

      conversation.triggerScrollToTop();
      setSelectionError(undefined);
      setClientFormError({kind: 'none'});
      setSelectedChannel(optionId);
      analytics.action('universal_help_channel_selection', {
        ...channelSelectionProperties(conversation.id, optionId),
        stage: 'selection_clicked',
      });

      if (optionId === 'channel_chat' || optionId === 'channel_restart') {
        setSubmitting(true);
        const selectedOption = displayContent.channel_options.find(
          (option) => option.id === optionId,
        );
        let response: unknown;
        try {
          analytics.action('universal_help_channel_selection', {
            ...channelSelectionProperties(conversation.id, optionId),
            stage: 'submission_started',
          });
          const responsePromise = submitChannelMenuOutcome({
            text: selectedOption?.message ?? optionId,
            channel: optionId,
          });
          analytics.action('universal_help_channel_selection', {
            ...channelSelectionProperties(conversation.id, optionId),
            stage: 'submission_dispatched',
          });
          response = await responsePromise;
          analytics.action('universal_help_channel_selection', {
            ...channelSelectionProperties(conversation.id, optionId),
            stage: submissionSucceeded(response)
              ? 'result_success'
              : 'result_error',
          });
          if (submissionSucceeded(response)) {
            setSubmitted(true);
            return;
          }
        } catch {
          analytics.action('universal_help_channel_selection', {
            ...channelSelectionProperties(conversation.id, optionId),
            stage: 'result_error',
          });
          // Show the same retry affordance as the legacy inline-data menu.
        } finally {
          setSubmitting(false);
        }
        showSelectionFailure(errorCodeFromResponse(response));
      }
    },
    [
      displayContent.channel_options,
      analytics,
      conversation,
      isDone,
      submitting,
      submitChannelMenuOutcome,
      showSelectionFailure,
    ],
  );

  const handleCancelForm = useCallback(() => {
    setSelectionError(undefined);
    setClientFormError({kind: 'none'});
    setSelectedChannel(undefined);
  }, []);

  const handleRacSubmit = useCallback(
    async (name: string, phoneNumber: string, msg: string) => {
      analytics.action('support_conversations_rac_submit');
      setClientFormError({kind: 'none'});
      setSubmitting(true);
      let response: unknown;
      try {
        response = await submitChannelMenuOutcome({
          text: [name, phoneNumber, msg].join('\n'),
          channel: 'channel_rac',
          rac_form: {
            name,
            phone_number: phoneNumber,
            message: msg,
          },
        });
      } catch {
        setClientFormError({
          kind: 'rac',
          message: intl.formatMessage(messages.racGenericError),
        });
        return;
      } finally {
        setSubmitting(false);
      }
      if (submissionSucceeded(response)) {
        setSubmitted(true);
        return;
      }
      const parsedError = parseChannelMenuServerError(
        errorCodeFromResponse(response),
        intl,
      );
      setClientFormError(
        parsedError.kind === 'rac'
          ? parsedError
          : {
              kind: 'rac',
              message: intl.formatMessage(messages.racGenericError),
            },
      );
    },
    [analytics, intl, submitChannelMenuOutcome],
  );

  const handleEmailSubmit = useCallback(
    async (values: SendUsAnEmailFormValues) => {
      setClientFormError({kind: 'none'});
      setSubmitting(true);
      const submissionContent = {
        text: buildEmailSubmissionText(values),
        channel: 'channel_email',
        email_form: {
          email: values.unauthenticatedEmail ?? '',
          body: values.question ?? '',
          subject: values.subject,
          cc_emails: values.ccEmails,
          attachment_ids: values.attachments?.map((a) => a.id) ?? [],
          request_issue_context: buildUHConversationIssueContext(conversation),
        },
      } as const;
      analytics.action('support_conversations_email_submit');
      let response: unknown;
      try {
        response = await submitChannelMenuOutcome(submissionContent);
      } catch {
        setClientFormError({kind: 'email', reason: 'submission'});
        return;
      } finally {
        setSubmitting(false);
      }
      if (submissionSucceeded(response)) {
        setSubmitted(true);
        return;
      }
      setClientFormError({kind: 'email', reason: 'submission'});
    },
    [analytics, conversation, submitChannelMenuOutcome],
  );

  const options: Option[] = useMemo(
    () =>
      displayContent.channel_options
        .filter((option) => !option.disabled)
        .map((option) => ({
          value: option.id,
          label: option.message,
          icon: getIconAsset(option.icon),
          onPress: () => handleSelectOption(option.id),
        })),
    [displayContent.channel_options, handleSelectOption],
  );

  const serverError = useMemo(
    () => parseChannelMenuServerError(content.error_code, intl),
    [content.error_code, intl],
  );

  const formError = useMemo((): ChannelMenuServerError => {
    if (clientFormError.kind !== 'none') {
      return clientFormError;
    }
    return serverError;
  }, [clientFormError, serverError]);

  // Report server-surfaced submission errors (e.g. rate limit, ineligible)
  // once per distinct error code, matching the legacy inline-data analytics.
  const reportedErrorCodeRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const errorCode = content.error_code;
    if (!errorCode) {
      reportedErrorCodeRef.current = undefined;
      return;
    }
    if (errorCode === reportedErrorCodeRef.current) {
      return;
    }
    reportedErrorCodeRef.current = errorCode;
    if (errorCode.startsWith('rac-')) {
      analytics.action('support_conversations_rac_server_error', {
        universalHelp: true,
      });
    } else if (errorCode.startsWith('email-')) {
      analytics.action('support_conversations_email_server_error', {
        universalHelp: true,
      });
    }
  }, [content.error_code, analytics]);

  // Keep completed discrete menu selections visible while the conversation
  // changes state. The selection starts as optimistic local state and is then
  // restored from the server-backed selected_option_id after the refetch.
  const showCompletedDiscreteSelection =
    isDone &&
    (selectedChannel === 'channel_chat' ||
      selectedChannel === 'channel_restart');
  const chatButtonOptionsProps = {
    options: showCompletedDiscreteSelection
      ? options.filter((option) => option.value === selectedChannel)
      : options,
    selectedOptionId: selectedChannel,
    disabled: isDone || submitting,
    error: selectionError,
    animated,
    conversation,
  };

  // Once submitted/done the menu and forms are hidden, leaving nothing to show.
  // Return null (rather than an empty container) so the surrounding
  // `.uh-conversations-message-added:empty` rule can collapse the row; an empty
  // container would otherwise still consume a flex gap and double the spacing.
  if (isDone && !showCompletedDiscreteSelection && !content.notice) {
    return null;
  }

  return (
    <view.div
      css={{
        stack: 'y',
        gap: 'medium',
      }}
    >
      {content.notice ? (
        <Banner
          type={mapNoticeType(content.notice_type)}
          description={content.notice}
        />
      ) : null}
      {!isDone || showCompletedDiscreteSelection ? (
        <ChatButtonOptions {...chatButtonOptionsProps} />
      ) : null}
      {isFormOpen && !isDone ? (
        <view.div
          data-testid="channel-menu-selected-panel"
          css={{
            stack: 'y',
            gap: 'medium',
            border: '1px solid',
            borderColor: 'border',
            borderRadius: 'medium',
            padding: 'medium',
          }}
        >
          {!isDone && selectedChannel === 'channel_rac' ? (
            <RequestACallForm
              initialName={content.initial_name}
              initialPhoneNumber={content.initial_phone_number}
              initialMessage={
                typeof conversation.context.latestQuestion === 'string'
                  ? conversation.context.latestQuestion
                  : undefined
              }
              handleSubmit={handleRacSubmit}
              handleCancel={handleCancelForm}
              loading={submitting}
              serverError={
                formError.kind === 'rac' ? formError.message : undefined
              }
            />
          ) : null}
          {!isDone && selectedChannel === 'channel_email' ? (
            <SendUsAnEmailForm
              showSubjectField={content.show_subject_field}
              showUnauthenticatedEmailField={
                content.show_unauthenticated_email_field
              }
              showCcEmailsField={content.show_cc_emails_field}
              initialUnauthenticatedEmail={
                content.initial_unauthenticated_email
              }
              initialQuestionValue={
                typeof conversation.context.latestQuestion === 'string'
                  ? conversation.context.latestQuestion
                  : undefined
              }
              handleSubmit={handleEmailSubmit}
              onCancel={handleCancelForm}
              isSubmissionPending={submitting}
              isRateLimitedError={
                formError.kind === 'email' &&
                formError.reason === 'rate_limited'
              }
              isSubmissionError={
                formError.kind === 'email' && formError.reason === 'submission'
              }
              hasUnauthenticatedEmailError={
                formError.kind === 'email' &&
                formError.reason === 'invalid_email'
              }
              hasCcEmailsError={
                formError.kind === 'email' &&
                formError.reason === 'invalid_cc_emails'
              }
            />
          ) : null}
        </view.div>
      ) : null}
    </view.div>
  );
}
